// @flow

import React, { Fragment } from 'react';
import type { Node } from 'react';
import classNames from 'classnames';
import { MediaQuery, Button, Heading, Paragraph, Link } from '@riseart/common';
import { Wrapper } from '@riseart/layout';
import { Banner, getBannerMedia } from 'shared_components/common/banner/Banner';
import { renderTextByLines } from 'shared_services/riseart/utils/Utils';

import {
  raScreenXsMax,
  raScreenSm,
  raScreenMdMax,
  raScreenLg,
} from '@riseart/antd-provider/dist/website/variables.less';
import {
  cmsModuleBannerCls,
  cmsModuleBannerCenterCls,
  cmsModuleBannerRightCls,
  cmsModuleBannerContentCls,
  cmsModuleBannerContentBottomCls,
  cmsModuleBannerParagraphCls,
  cmsModuleBannerTextCls,
  cmsModuleBannerButtonCls,
  cmsModuleBannerButtonNormalCls,
  cmsModuleBannerButtonNegativeCls,
} from 'shared_components/cms/modules/banner/Banner.less';

type Props = {
  size: 'small' | 'medium' | 'large',
  title?: ?string,
  subTitle?: ?string,
  description?: ?string,
  htmlContent?: ?string,
  align?: 'left' | 'center' | 'right',
  customButton: Node,
  button?: ?Object,
  media: Object,
  detail?: Object,
};

/**
 * CmsModuleBanner
 *
 * @param {Props} props
 * @returns {Node}
 */
export const CmsModuleBanner = ({
  size = 'large',
  title = null,
  subTitle = null,
  description = null,
  htmlContent = null,
  align = 'left',
  customButton = null,
  button = null,
  detail,
  media,
}: Props): Node => {
  const BUTTON_STYLE_CLASSNAME = {
    normal: cmsModuleBannerButtonNormalCls,
    negative: cmsModuleBannerButtonNegativeCls,
  };

  return (
    <Banner
      size={size}
      altTitle={title || ''}
      artDirectionType="banner.fullWidth"
      artDirectionKey="common.banner"
      media={getBannerMedia(media, [])}
      className={classNames(cmsModuleBannerCls, {
        [cmsModuleBannerCenterCls]: align === 'center',
        [cmsModuleBannerRightCls]: align === 'right',
      })}
    >
      {(title && title.length) ||
      subTitle ||
      description ||
      customButton ||
      button ||
      htmlContent ? (
        <Fragment>
          {detail ? (
            <div className={cmsModuleBannerContentBottomCls}>
              <Wrapper>
                <Link
                  type="light"
                  to={detail.href}
                  external={!!detail.external}
                  title={detail.text}
                  {...(detail.target ? { target: detail.target } : {})}
                >
                  {detail.text}
                </Link>
              </Wrapper>
            </div>
          ) : null}
          <div className={cmsModuleBannerContentCls}>
            <Wrapper>
              <MediaQuery maxWidth={raScreenXsMax}>
                {(isPhone) => (
                  <MediaQuery minWidth={raScreenSm} maxWidth={raScreenMdMax}>
                    {(isTablet) => (
                      <MediaQuery minWidth={raScreenLg}>
                        {(isDesktop) => {
                          const color =
                            (isPhone && media && media.small && media.small.font) ||
                            (isTablet && media && media.medium && media.medium.font) ||
                            (isDesktop && media && media.large && media.large.font) ||
                            null;

                          return (
                            <div
                              className={cmsModuleBannerTextCls}
                              {...(color ? { style: { color } } : null)}
                            >
                              {/* Show custom HTML */}
                              {htmlContent ? (
                                <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
                              ) : (
                                // Show separate components if no custom HTML is provided
                                <Fragment>
                                  {title ? (
                                    <Heading tag="h1" level="1">
                                      {renderTextByLines(title)}
                                    </Heading>
                                  ) : null}
                                  {subTitle ? (
                                    <Heading tag="h2" level="3">
                                      {renderTextByLines(subTitle)}
                                    </Heading>
                                  ) : null}
                                  {description ? (
                                    <Paragraph className={cmsModuleBannerParagraphCls}>
                                      {renderTextByLines(description)}
                                    </Paragraph>
                                  ) : null}
                                </Fragment>
                              )}
                              {!customButton && button ? (
                                <Button
                                  isAnchor
                                  type={button.type || 'ghost'}
                                  size={button.size || 'xlarge'}
                                  {...(button.width ? { width: button.width } : null)}
                                  {...(button.target ? { target: button.target } : null)}
                                  external={button.external || false}
                                  href={button.href}
                                  className={classNames(
                                    cmsModuleBannerButtonCls,
                                    BUTTON_STYLE_CLASSNAME[button.style],
                                  )}
                                >
                                  {button.text}
                                </Button>
                              ) : null}
                              {customButton || null}
                            </div>
                          );
                        }}
                      </MediaQuery>
                    )}
                  </MediaQuery>
                )}
              </MediaQuery>
            </Wrapper>
          </div>
        </Fragment>
      ) : null}
    </Banner>
  );
};
